import { openSentryFeedbackForm, type SentryFeedbackOverrides } from '@liza/ui/utils/openSentryFeedbackForm';
import { useEffect, useRef } from 'react';

/**
 * Opens the Sentry user-feedback form when the user presses ⌘/ (Ctrl+/ on
 * Windows/Linux). The feedback integration is initialized with
 * `autoInject: false`, so this is the keyboard entry point that mirrors the
 * command-palette action and the "Report issue" navigation buttons.
 */
export const useSentryFeedbackHotkey = (optionOverrides?: SentryFeedbackOverrides): void => {
  // Hold the latest overrides in a ref so changing labels (e.g. on a locale
  // switch) doesn't tear down and rebind the keydown listener every render.
  const overridesRef = useRef(optionOverrides);
  overridesRef.current = optionOverrides;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== '/' || !(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
        return;
      }
      event.preventDefault();
      openSentryFeedbackForm(overridesRef.current);
    };

    document.addEventListener('keydown', handleKeyDown);
    return (): void => document.removeEventListener('keydown', handleKeyDown);
  }, []);
};
